import Lozenge, {type LozengeProps, type ThemeAppearance} from "@atlaskit/lozenge";
import {token} from "@atlaskit/tokens";

export type AnankeLozengeAppearance = ThemeAppearance;

/** Kanban/list status lozenge: subtle fill. Border defaults to `color` unless `borderColor` is set. */
export type AnankeAccentStatusLozengeStyle = {
    backgroundColor: string;
    color: string;
    borderColor?: string;
};

/** Title-style label (e.g. "NON ENGAGED" → "Non Engaged"). */
export function formatAnankeLozengeLabel(label: string): string {
    const trimmed = label.trim();
    if (trimmed === "" || trimmed === "-") {
        return trimmed;
    }
    return trimmed
        .toLowerCase()
        .replace(/(^|[\s-])([a-z])/g, (_match, separator, letter) => `${separator}${letter.toUpperCase()}`);
}

export function AnankeLozenge({
    children,
    appearance = "default",
    isBold = false,
    style,
    maxWidth = 200,
    testId,
}: LozengeProps) {
    return (
        <Lozenge appearance={appearance} isBold={isBold} style={style} maxWidth={maxWidth} testId={testId}>
            {children}
        </Lozenge>
    );
}

/**
 * Accent status lozenge for list cards and anywhere kanban `columnConfig` colors apply.
 * Does not use ADS Lozenge (its subtle appearance adds a fixed gray border that clashes with kanban accents).
 * One border stroke, title-cased label, regular weight.
 */
export function AnankeAccentStatusLozenge({
    children,
    style,
    maxWidth = 200,
    testId,
    formatLabel = true,
}: {
    children: string;
    style: AnankeAccentStatusLozengeStyle;
    maxWidth?: LozengeProps["maxWidth"];
    testId?: string;
    formatLabel?: boolean;
}) {
    const accentColor = style.color;
    const borderColor = style.borderColor ?? accentColor;
    const label = formatLabel === true ? formatAnankeLozengeLabel(children) : children;
    const maxWidthValue = typeof maxWidth === "string" ? maxWidth : `${maxWidth}px`;

    return (
        <span
            data-testid={testId}
            className="box-border inline-flex w-fit max-w-full shrink-0 whitespace-nowrap normal-case"
            style={{
                border: `0.5px solid ${borderColor}`,
                borderRadius: "3px",
                backgroundColor: style.backgroundColor,
                color: accentColor,
                paddingInline: token("space.050"),
                fontFamily: token("font.family.body"),
                fontSize: "11px",
                fontStyle: "normal",
                fontWeight: token("font.weight.regular"),
                lineHeight: "16px",
                width: "fit-content",
                maxWidth: maxWidthValue,
            }}
        >
            {label}
        </span>
    );
}
